import {usePromotion} from '@/context/Promotion/PromotionContext'
import {FreeProductReward, PromotionCondition, RewardType} from '@/types/promotion'
import {ANY_ID, BuyType} from '@/utils/constants/promotionConstants'
import {useMemo, useState} from 'react'
import {useRewardValidations} from './RewardValidations.js'
import {toRewardsUpdate} from './toRewardsUpdate'

// Free products can be picked by product ID or GTIN, or through category/brand selection tabs
const freeProductTypeOptions = [
  {displayText: 'Product ID', id: BuyType.PRODUCT_ID},
  {displayText: 'GTIN', id: BuyType.GTINS},
  {
    displayText: 'Category',
    subTypes: [
      {tabLabel: 'Category', id: 'CATEGORY'},
      {tabLabel: 'Sub Category', id: 'SUBCATEGORY'},
    ],
    id: BuyType.CATEGORY,
  },
  {
    displayText: 'Brand',
    subTypes: [
      {tabLabel: 'Brand', dropDownType: 'brands', id: 'BRAND'},
      {tabLabel: 'Sub Brand', dropDownType: 'sub_brands', id: 'SUBBRAND'},
    ],
    id: BuyType.BRAND,
  },
]

type UseRewardFreeProductsArgs = {
  promotionCondition: PromotionCondition
  rewardIndex?: number
}

type SelectedItem = {id: string; displayText: string}

export const useRewardFreeProducts = ({promotionCondition, rewardIndex = 0}: UseRewardFreeProductsArgs) => {
  const {updateCondition, promotionConfig, isLoadingConfig, promotion} = usePromotion()
  const {hasValueLayerError} = useRewardValidations(promotionCondition, rewardIndex)
  const buyTypeOptions = (promotionConfig?.conditions?.itemTypeOptions ?? []).filter(option => option.id !== ANY_ID)
  const buyTypesIds = buyTypeOptions.map(item => item.id)
  const reward = (promotionCondition?.rewards?.[rewardIndex] ?? {}) as FreeProductReward

  const [modalProps, setModalProps] = useState({show: false, type: ''})

  const updateReward = (update: Partial<FreeProductReward & {selectedItems?: SelectedItem[]}>) => {
    const currentReward = promotionCondition?.rewards?.[rewardIndex] ?? {}

    updateCondition({
      sequence: promotionCondition.sequence,
      rewards: toRewardsUpdate({...currentReward, ...update} as FreeProductReward, rewardIndex),
    })
  }

  const handleBuyCount = values => {
    if (values?.[0]?.id) {
      updateReward({quantity: Number(values[0].id)})
    }
  }

  const clearValues = () => {
    updateReward({items: []})
  }

  // Selecting a product type opens the selection modal; picking/removing listed products applies them directly
  const handleSelectedProductType = values => {
    if (!values || !values.length) {
      clearValues()
      return
    }

    const isModalValue = values.find(item => buyTypesIds.includes(item.id))

    if (isModalValue) {
      if (isModalValue.id === ANY_ID) {
        clearValues()
        return
      }
      setModalProps({show: true, type: isModalValue.id})
    } else {
      const items = values.map(item => ({id: item.id, name: item.displayText}))
      const selectedItems = values.map(item => ({id: item.id, displayText: item.displayText}))
      updateReward({items, selectedItems})
    }
  }

  const handleCloseModal = () => {
    setModalProps(prev => ({...prev, show: false}))
  }

  const handleSubmitModal = values => {
    const selectedItems = values?.values || []
    const items = selectedItems.map(item => ({id: item.id, name: item.displayText}))
    updateReward({items, selectedItems, itemType: values.itemType})
    handleCloseModal()
  }

  const buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  // Products are shared across layers, so they are always read from the first layer
  const firstReward = promotion?.promotionConditions?.[0]?.rewards?.[rewardIndex]
  const items = firstReward?.type === RewardType.FREE_PRODUCT ? firstReward.items : []
  const productsValue = items.map(item => ({id: item.id, displayText: item.name}))

  return {
    isLoadingConfig,
    productTypeOptions: freeProductTypeOptions,
    buyQtyOptions,
    quantityValue: buyQtyOptions?.filter(item => item?.id === reward.quantity) ?? [],
    productsValue,
    selectedValuesText: items.map(item => item.name).join(', '),
    isError: hasValueLayerError(),
    modalProps,
    modalConditionValues: {values: productsValue, itemType: reward.itemType || ''},
    handleBuyCount,
    handleSelectedProductType,
    handleSubmitModal,
    handleCloseModal,
  }
}
